import { FC, useEffect, useRef, useState } from "react";
import { KTSVG } from "../../_metronic/helpers";
import { useFormik } from "formik";
import * as Yup from 'yup';
import Swal from "sweetalert2";
import { changePhoneUser, createPrimaryPhone, getUserByToken } from "../../app/modules/auth/redux/AuthCRUD";
import { useIntl } from "react-intl";
import useHandleCloseModal from "../../hooks/HandleCloseModal";
import useAccessToken from "../../hooks/AccessToken";
import useUser from "../../hooks/User";
import * as auth from '../../app/modules/auth/redux/AuthRedux'
import { useDispatch } from "react-redux";
import { AdditionalPhones, PhonesType } from "../../app/modules/auth/models/UserModel";
import { clearDB } from "../../db";
import InputPhoneNative from "../Input/InputPhoneNative";
import useCheckWa from "../../hooks/useCheckWa";
import { useChatStore } from "../../stores/ChatStore";
import VirtualizedSelect, { VirtualizedSelectOption, VirtualizedSelectOptionsType } from "../Select/SelectVirtualized";
import useSWR from "swr";
import axios from "axios";
import { SelectOption } from "../../interfaces/SelectOption";
import { UserCompany } from "../../interfaces/UserCompany";
import clsx from "clsx";
import useModalEffect from "../../hooks/useModalEffect";
import { OTPModal } from "../OTPModal";
import { deleteAdditionalPhone, sendOtpAdditionalPhone } from "../../api/AdditionalPhone";

const ChangePhoneModal: FC = () => {
    const API_URL = process.env.REACT_APP_API_URL
    const user = useUser();
    const getPrimaryPhone = user.data.phones ? user.data.phones.find(phone => phone.isPrimary) : null;
    const token = useAccessToken();
    const modalRef = useRef<HTMLDivElement | null>(null);
    const intl = useIntl();
    const closeModalRef = useRef<HTMLButtonElement | null>(null);
    const [isPrimaryPhoneChanged, setIsPrimaryPhoneChanged] = useState(false);
    const [additionalPhones, setAdditionalPhones] = useState<AdditionalPhones[]>([]);
    const [optionUser, setOptionUser] = useState<SelectOption[]>([]);
    const [valuePrimaryPhone, setValuePrimaryPhone] = useState(getPrimaryPhone?.number || '');
    const [isLoadingPrimaryPhone, setIsLoadingPrimaryPhone] = useState(false);
    const [isOtpModal, setIsOtpModal] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<AdditionalPhones>({} as AdditionalPhones);
    const [requestId, setRequestId] = useState('');
    const [primaryPhoneError, setPrimaryPhoneError] = useState('');
    const { checkWa, checkWaValid, setCheckWa } = useCheckWa({
        phoneNumber: valuePrimaryPhone,
    });
    const dispatch = useDispatch();
    const [quickChangeValuePhone, setQuickChangeValuePhone] = useState<PhonesType>({ number: '', label: '', isPrimary: false });
    const { originalChatPhone, setOriginalChatPhone } = useChatStore()

    const fetchUserData = () => {
        if (user && user.data.phones) {
            setValuePrimaryPhone(getPrimaryPhone?.number || '');

            let additional: AdditionalPhones[] = user.data.phones.filter(phone => !phone.isPrimary).map((data: PhonesType) => ({
                id: data.id!,
                number: data.number,
                name: data.label,
                isVerified: true
            }))
            setAdditionalPhones(additional);
        }
    };

    const fetcher = (url: string) => axios.get(url, {
        headers: {
            'X-Auth-token': token
        }
    }).then(res => res.data.data)

    const { data: usersCompany = [], isLoading: userLoadingCompany } = useSWR(`${API_URL}v2/chat/access/chat-request/users`, fetcher)

    useEffect(() => {
        fetchUserData();
    }, [user]);

    useEffect(() => {
        if (!userLoadingCompany) {
            const options = usersCompany.map((user: UserCompany) => ({
                id: user.id,
                value: user.phone,
                label: user.name,
            }))
            setOptionUser(options)
        }
    }, [usersCompany]);

    const validatePrimaryPhone = () => {
        if (!valuePrimaryPhone) {
            setPrimaryPhoneError(intl.formatMessage({ id: "FORM.VALIDATION.REQUIRED" }));
            return false;
        }
        if (valuePrimaryPhone.length < 10) {
            setPrimaryPhoneError(intl.formatMessage({ id: "FORM.VALIDATION.PHONE_MIN_LENGTH" }));
            return false;
        }
        setPrimaryPhoneError('');
        return true;
    };

    const handleQuickChangePhone = (value: string, index: number) => {
        Swal.fire({
            icon: 'warning',
            title: intl.formatMessage({ id: "FORM.VALIDATION.CHANGE_PRIMARY_PHONE" }, { title: value }),
            showCancelButton: true,
            confirmButtonText: intl.formatMessage({ id: "FORM.CONFIRMATION.YES" }),
        }).then((result) => {
            if (result.isConfirmed) {
                const newPhone = { number: value, label: `Primary Phone ${index + 1}`, isPrimary: true };
                setQuickChangeValuePhone(newPhone);
                handleSubmitPrimaryPhone(newPhone);
            }
        })
    }

    const handleSubmitPrimaryPhone = async (quickChangeValuePhone: PhonesType) => {
        if (!validatePrimaryPhone()) return;

        setIsLoadingPrimaryPhone(true);
        try {
            const checkQuickChangePhone = quickChangeValuePhone.number !== '' ? quickChangeValuePhone : null;
            const response = await createPrimaryPhone(checkQuickChangePhone ? checkQuickChangePhone.number : valuePrimaryPhone, token);
            if (response.data.success) {
                const updatedUser = {
                    ...user,
                    data: { ...user.data, phone: response.data.data.phone, phones: response.data.data.phones }
                };
                dispatch(auth.actions.setUser(updatedUser));
                setValuePrimaryPhone(response.data.data.phone);
                clearDB();
                Swal.fire({
                    icon: 'success',
                    title: intl.formatMessage({ id: "ALERT.TITLE.SUCCESS" }),
                    text: intl.formatMessage({ id: "FORM.VALIDATION.CHANGED_PRIMARY_PHONE" }),
                });
            }
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: error.response.data.message || 'An error occurred while updating phone numbers'
            });
            setValuePrimaryPhone(getPrimaryPhone?.number || ''); // ketika error mengembalikan nilai primary phone sebelumnya
        } finally {
            setIsLoadingPrimaryPhone(false);
            setIsPrimaryPhoneChanged(false);
            setCheckWa('');
            setQuickChangeValuePhone({ number: '', label: '', isPrimary: false });
        }
    };

    useEffect(() => {
        const modalElement = modalRef.current;
        const handleModalHidden = () => {
            setIsPrimaryPhoneChanged(false);
            fetchUserData();
        };

        const handleModalShow = () => {
            fetchUserData();
        };

        modalElement?.addEventListener('hidden.bs.modal', handleModalHidden);
        modalElement?.addEventListener('show.bs.modal', handleModalShow);

        return () => {
            modalElement?.removeEventListener('hidden.bs.modal', handleModalHidden);
            modalElement?.removeEventListener('show.bs.modal', handleModalShow);
        };
    }, [user]);

    // useEffect(() => {
    //     const options = usersCompany.map((user: UserCompany) => {
    //         if(additionalPhones.find(phone => phone.number === user.phone)) return null
    //         else {
    //             return {
    //                 id: user.id,
    //                 value: user.phone,
    //                 label: user.name,
    //             }
    //         }
    //     })
    //     setOptionUser(options)
    // }, [additionalPhones])

    useModalEffect({
        showModal: isOtpModal,
        setShowModal: setIsOtpModal,
        modalId: `otp-modal-additionalPhone`
    })

    const handleIsPhoneChanged = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsPrimaryPhoneChanged(!isPrimaryPhoneChanged);
    };

    const handleAddNewPhone = () => {
        setAdditionalPhones([
            { id: '', number: '', name: '', isVerified: false },
            ...additionalPhones,
        ]);
    };

    const handleChangeUser = (option: SelectOption, index: number) => {
        additionalPhones[index].id = option === null ? '' : option.id!
        additionalPhones[index].number = option === null ? '' : option.value
        additionalPhones[index].name = option === null ? '' : option.label
        setAdditionalPhones([...additionalPhones])
    }

    const handleVerifedPhone = async (data: AdditionalPhones, index: number) => {
        try {
            const response = await sendOtpAdditionalPhone(data.id, token);
            if (response.data.success) {
                setRequestId(response.data.data.requestId)
                setSelectedUser(data)
                setIsOtpModal(true)
            }
        } catch (error: any) {
            Swal.fire({
                target: document.getElementById('manage-user-phone-modal'),
                icon: 'error',
                title: error.response.data.message,
                confirmButtonText: 'Ok'
            })
        }
    };

    const handleDeletePhone = (data: AdditionalPhones, index: number) => {
        Swal.fire({
            title: intl.formatMessage({ id: "FORM.ACTION.DELETE" }),
            html: `<p>${intl.formatMessage({ id: "FORM.CONFIRMATION" })} ${intl.formatMessage({ id: "FORM.ACTION.DELETE" }).toLowerCase()} ${data.name} ?</p>`,
            icon: "warning",
            showCancelButton: !0,
            buttonsStyling: !1,
            confirmButtonText: intl.formatMessage({ id: "FORM.ACTION.DELETE" }),
            cancelButtonText: intl.formatMessage({ id: "FORM.CONFIRMATION.NO" }),
            showLoaderOnConfirm: true,
            customClass: {
                confirmButton: 'btn btn-danger',
                cancelButton: 'btn btn-active-light',
            },
            target: document.getElementById('manage-user-phone-modal'),
            reverseButtons: true,
            heightAuto: false,
            preConfirm: async () => {
                if (data.isVerified) {
                    try {
                        const response = await deleteAdditionalPhone(data.id, token)
                        if (response.data.success) {
                            const { data: user } = await getUserByToken(token as string)
                            setOriginalChatPhone(user.data.chat_phone)
                            dispatch(auth.actions.fulfillUser(user))

                            Swal.fire({
                                title: 'Success',
                                target: document.getElementById('manage-user-phone-modal'),
                                text: intl.formatMessage({ id: "FORM.VALIDATION.DELETE_SUCCESS" }),
                                icon: 'success',
                                showConfirmButton: false,
                                timer: 1500
                            })
                        }
                    } catch (error: any) {
                        Swal.fire({
                            target: document.getElementById('manage-user-phone-modal'),
                            icon: 'error',
                            title: error.response.data.message,
                            confirmButtonText: 'Ok',
                        })
                    }
                } else {
                    const updatedPhones = [...additionalPhones];
                    updatedPhones.splice(index, 1);
                    setAdditionalPhones(updatedPhones);
                }
            },
            allowOutsideClick: () => !Swal.isLoading(),
        })
    };

    const CustomSelectUserOption = (props: { option: VirtualizedSelectOptionsType; onClick?: () => void }) => {
        const { option } = props;

        return (
            <div className="d-flex align-items-center px-3 h-100" style={{ cursor: 'pointer', background: option.value === 'chocolate' ? '#d9d9d9' : 'transparent', }}>
                <span className="align-middle">{option!.label} / <b>{option!.value}</b></span>
            </div>
        );
    }

    const CustomSelectSingleUserValue = (props: { value: VirtualizedSelectOption; placeholder?: string }) => {
        const { value, placeholder } = props;
        if (!value?.value) return <span>{placeholder}</span>

        return (
            <span className="align-middle">{value!.label} / <b>{value!.value}</b></span>
        );
    }

    return (
        <>
            <div className="modal fade" tabIndex={-1} id="manage-user-phone-modal" ref={modalRef}>
                {
                    isLoadingPrimaryPhone && (
                        <div className="modal-backdrop fade show" style={{ zIndex: 1000 }}>
                            <div className="spinner spinner-md spinner-primary" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1000 }}></div>
                        </div>
                    )
                }
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{intl.formatMessage({ id: "MENU.MANAGE_PHONES" })}</h5>
                            <div className="btn btn-icon btn-sm btn-active-light-primary ms-2" onClick={() => modalRef.current?.click()}>
                                <KTSVG path="/media/icons/duotune/arrows/arr061.svg" className="svg-icon svg-icon-2x" />
                            </div>
                        </div>
                        <div className="modal-body fs-6">
                            <label className="form-label fs-6 fw-bolder text-dark">{intl.formatMessage({ id: "FORM.LABEL.PRIMARY_PHONE" })}</label>
                            <div className="flex-grow-1">
                                <div className="d-flex justify-content-center align-items-center gap-3">
                                    <InputPhoneNative
                                        value={valuePrimaryPhone}
                                        setValue={(val) => {
                                            setValuePrimaryPhone(val);
                                            setPrimaryPhoneError('');
                                        }}
                                        hasDisabled={!isPrimaryPhoneChanged}
                                        checkWaValid={checkWaValid}
                                        validatePrimaryPhone={validatePrimaryPhone}
                                    />
                                    {/* {isPrimaryPhoneChanged ? (
                                        <>
                                            <button
                                                type="button"
                                                className={`btn btn-sm btn-success ${isLoadingPrimaryPhone ? "disabled" : ""}`}
                                                data-kt-indicator={isLoadingPrimaryPhone ? 'on' : 'off'}
                                                onClick={() => handleSubmitPrimaryPhone(quickChangeValuePhone)}
                                                disabled={!!primaryPhoneError || checkWa === 'false'}
                                            >
                                                <span className="indicator-label">
                                                    {intl.formatMessage({ id: "FORM.ACTION.SAVE" })}
                                                </span>
                                                <span className="indicator-progress">
                                                    Loading..
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-light"
                                                onClick={handleIsPhoneChanged}
                                            >
                                                {intl.formatMessage({ id: "FORM.VALIDATION.CANCEL" })}
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary"
                                            onClick={handleIsPhoneChanged}
                                        >
                                            {intl.formatMessage({ id: "FORM.LABEL.CHANGE" })}
                                        </button>
                                    )} */}
                                </div>
                                {primaryPhoneError && (
                                    <div className="text-danger mt-1">
                                        {primaryPhoneError}
                                    </div>
                                )}
                                {checkWa && (
                                    <div className={`mt-1 ${checkWa === 'true' ? 'text-success' : 'text-danger'}`}>
                                        {checkWa === 'true'
                                            ? intl.formatMessage({ id: "FORM.VALIDATION.VALID_WHATSAPP" })
                                            : checkWa === 'false'
                                                ? intl.formatMessage({ id: "FORM.VALIDATION.INVALID_WHATSAPP" })
                                                : null
                                        }
                                    </div>
                                )}
                                <div className="mt-3 mb-3 d-flex justify-content-between align-items-center">
                                    <label className="form-label mb-0 fs-6 fw-bolder text-dark">{intl.formatMessage({ id: "FORM.LABEL.CHAT_PHONE" })}</label>
                                    <button type="button" className="btn btn-primary btn-sm" onClick={handleAddNewPhone}>
                                        <i className="fas fa-plus"></i> {intl.formatMessage({ id: "FORM.ACTION.ADD" })}
                                    </button>
                                </div>
                            </div>
                            {
                                additionalPhones.map((data, index) => (
                                    <div key={index} className="d-flex gap-2 mb-2 align-items-center">
                                        <VirtualizedSelect
                                            key={index}
                                            options={optionUser}
                                            isLoading={userLoadingCompany}
                                            className={clsx("w-100")}
                                            placeholder={`${intl.formatMessage({ id: "FORM.ACTION.CHOOSE" })} ${intl.formatMessage({ id: 'FORM.LABEL.USER' })}`}
                                            value={optionUser.find(option => option.id === data.id) || null}
                                            onChange={(option: VirtualizedSelectOption) => { handleChangeUser(option, index) }}
                                            isDisabled={data.isVerified}
                                            isClearable={data.isVerified ? false : true}
                                            components={
                                                {
                                                    Option: CustomSelectUserOption,
                                                    SingleValue: CustomSelectSingleUserValue,
                                                }
                                            }
                                        />
                                        {
                                            !data.isVerified && data.number && (
                                                <button
                                                    type="button"
                                                    className="btn btn-light-success btn-icon"
                                                    style={{ height: '40px' }}
                                                    onClick={() => handleVerifedPhone(data, index)}
                                                >
                                                    <i className="fas fa-check-double"></i>
                                                </button>
                                            )
                                        }
                                        {
                                            data.id !== user.data.id &&
                                            <button
                                                type="button"
                                                className="btn btn-light-danger btn-icon"
                                                style={{ height: '40px' }}
                                                onClick={() => handleDeletePhone(data, index)}
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        }
                                    </div>
                                ))
                            }
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-light"
                                data-bs-dismiss="modal"
                                ref={closeModalRef}
                            >
                                {intl.formatMessage({ id: "FORM.ACTION.CLOSE" })}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {
                isOtpModal && <OTPModal username={user.data.username} password="" phone={selectedUser.number} type={"additionalPhone"} id={selectedUser.id} requestId={requestId} />
            }
        </>
    );
};

export { ChangePhoneModal };